import { Actuador, type AccionActuador } from '../../domain/entities/Actuador';
import { traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aFecha, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';

const VISTA = 'v3_vw_actuadores_control_ui';
const VISTA_HISTORIAL = 'v3_vw_actuadores_historial_ui';

/** Órdenes que entiende el servidor. */
export type TipoComando = 'set' | 'pulse';

export interface FiltrosActuadores {
  clienteId?: number | null;
  areaId?: number | null;
  nodoId?: number | null;
}

/** Una línea del historial de accionamientos. */
export interface Accionamiento {
  fecha: Date | null;
  orden: string;
  valor: string | null;
  quien: string | null;
  origen: string | null;
  resultado: string | null;
  aplicado: boolean | null;
  aplicadoEn: Date | null;
}

/** Confirmación que devuelve el equipo tras aplicar una orden. */
export interface Confirmacion {
  actuadorId: number;
  aplicado: boolean;
  estado: string | null;
}

/** Las acciones llegan como lista, como texto JSON o separadas por comas. */
function normalizarAcciones(valor: unknown): AccionActuador[] {
  let bruto: unknown = valor;

  if (typeof valor === 'string') {
    try {
      bruto = JSON.parse(valor);
    } catch {
      bruto = valor.split(/\s*,\s*|\s+/).filter(Boolean);
    }
  }

  if (!Array.isArray(bruto)) return [];

  const validas: AccionActuador[] = ['set', 'pulse'];
  return bruto
    .map((a) => String(a).toLowerCase() as AccionActuador)
    .filter((a) => validas.includes(a));
}

function aActuador(fila: Record<string, unknown>): Actuador {
  const id = aNumero(fila.id) ?? aNumero(fila.actuador_id) ?? 0;

  return new Actuador({
    id,
    nombre: aTexto(fila.nombre) ?? aTexto(fila.nombre_actuador) ?? `Actuador ${id}`,
    codigoEnvio: aTexto(fila.codigo_envio),
    tipoNombre: aTexto(fila.tipo_nombre),
    tipoCodigo: aTexto(fila.tipo_codigo),
    acciones: normalizarAcciones(fila.acciones),
    // Si la vista no se pronuncia, se asume que sí se puede accionar.
    puedeAccionar: aBooleano(fila.puede_accionar) ?? true,
    vigenciaSegundos: aNumero(fila.ttl_s),
    // Los estados se pasan sin convertir: pueden ser texto, booleano, número
    // u objeto, y la entidad sabe leer las cuatro formas.
    estadoFisico: fila.physical_state,
    origenFisico: aTexto(fila.physical_source),
    fechaFisico: aFecha(fila.physical_at),
    estadoReportado: fila.reported_state,
    origenReportado: aTexto(fila.reported_source),
    fechaReportado: aFecha(fila.reported_at),
    estadoDeseado: fila.desired_state,
    fechaUltimaConfirmacion: aFecha(fila.last_ack_applied_at),
    estadoSeguro: fila.safe_state,
    estadoVista: fila.estado_ui ?? fila.last_value,
    ultimaOrden: aTexto(fila.last_cmd_kind),
    nodoId: aNumero(fila.nodo_id),
    nodoNombre: aTexto(fila.nodo_nombre),
    areaId: aNumero(fila.area_id),
    areaNombre: aTexto(fila.area_nombre),
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
    actualizadoEn: aFecha(fila.updated_at),
  });
}

/**
 * Acceso a los actuadores y envío de órdenes.
 *
 * Las órdenes no escriben en la tabla: se encolan mediante una función del
 * servidor que comprueba permisos, registra quién la envió y decide cuándo
 * caduca. El equipo la aplica y responde con una confirmación aparte.
 */
export class RepositorioActuadores {
  async listar(filtros: FiltrosActuadores = {}): Promise<Actuador[]> {
    let consulta = filtroDemo.aplicar(VISTA, supabase.from(VISTA).select('*'));

    consulta = ambitoSesion.aplicar(consulta);
    if (filtros.clienteId) consulta = consulta.eq('cliente_id', filtros.clienteId);
    if (filtros.areaId) consulta = consulta.eq('area_id', filtros.areaId);
    if (filtros.nodoId) consulta = consulta.eq('nodo_id', filtros.nodoId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map(aActuador)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /** Relee un actuador concreto, para refrescar su estado tras una orden. */
  async obtenerPorId(id: number): Promise<Actuador | null> {
    const { data, error } = await supabase.from(VISTA).select('*').eq('id', id).maybeSingle();

    if (error) throw traducirErrorSupabase(error);
    return data ? aActuador(data as Record<string, unknown>) : null;
  }

  /**
   * Encola una orden.
   *
   * Devolver sin error significa que el servidor la aceptó, no que el equipo
   * la haya aplicado: eso llega después como confirmación.
   */
  async enviarOrden(
    actuadorId: number,
    tipo: TipoComando,
    carga: Record<string, unknown>,
  ): Promise<void> {
    const { error } = await supabase.rpc('v2_enqueue_actuador_cmd', {
      p_actuador_id: actuadorId,
      p_cmd_kind: tipo,
      p_payload: carga,
      p_not_before: null,
      p_expires_at: null,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  encender(actuadorId: number): Promise<void> {
    return this.enviarOrden(actuadorId, 'set', { v: 'ON' });
  }

  apagar(actuadorId: number): Promise<void> {
    return this.enviarOrden(actuadorId, 'set', { v: 'OFF' });
  }

  pulsar(actuadorId: number, milisegundos: number): Promise<void> {
    return this.enviarOrden(actuadorId, 'pulse', { pulse_ms: milisegundos });
  }

  /** Últimos accionamientos de un actuador. */
  async historial(actuadorId: number, limite = 25): Promise<Accionamiento[]> {
    const { data, error } = await supabase
      .from(VISTA_HISTORIAL)
      .select('*')
      .eq('actuador_id', actuadorId)
      .order('created_at', { ascending: false })
      .limit(limite);

    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[]).map((fila) => ({
      fecha: aFecha(fila.created_at),
      orden: aTexto(fila.cmd_kind) ?? '—',
      valor: aTexto(fila.valor_ui),
      quien: aTexto(fila.actor_label),
      origen: aTexto(fila.actor_source),
      resultado: aTexto(fila.resultado_ui) ?? aTexto(fila.status),
      aplicado: aBooleano(fila.applied),
      aplicadoEn: aFecha(fila.applied_at),
    }));
  }

  /**
   * Interpreta una confirmación recibida en tiempo real.
   * Se considera aplicada sólo si el equipo lo dice explícitamente.
   */
  static leerConfirmacion(fila: Record<string, unknown>): Confirmacion | null {
    const actuadorId = aNumero(fila.actuador_id);
    if (actuadorId === null) return null;

    const estado = aTexto(fila.status);
    const aplicado = aBooleano(fila.applied) === true && (estado === 'ok' || estado === 'applied');

    return { actuadorId, aplicado, estado };
  }
  /**
   * Guarda la configuración propia del actuador (el ajuste fino de su tipo).
   *
   * Va por una función del servidor porque es ella quien valida cada campo
   * contra el esquema del tipo; replicar esa validación aquí garantizaría que
   * tarde o temprano las dos copias discreparan.
   */
  async configurar(actuadorId: number, config: Record<string, unknown>): Promise<void> {
    const { error } = await supabase.rpc('v2_update_actuador_config', {
      p_actuador_id: actuadorId,
      p_config: config,
      p_strict_required: false,
    });

    if (error) throw traducirErrorSupabase(error);
  }
}

export const repositorioActuadores = new RepositorioActuadores();
